import { useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import { getTenantZone } from '../../locale'
import { viewTheme } from '../../views/theme'
import { isOpenNow, localDayMinute, weekRows } from './openingHoursSummary'
import * as Popover from '@radix-ui/react-popover'
import { useQuery } from '@tanstack/react-query'
import { attributesQuery } from '../../api/typeMetadata'
import { Button } from '../Button'
import { registerWidget, type CellContentProps, type CellEditorProps } from '../registry'
import { controlChrome, editorBehavior } from '../editorChrome'
import { ISO_WEEK, defaultProjection, runProjection, type ProjectionSpec } from '../display/projections'
import { escapeClosesOwnLayer } from '../../collection/filter/popoverEscape'
import {
  closedWeek,
  copyDay,
  dayName,
  validateWeek,
  valueFromWeek,
  weekFromValue,
  type DayHours,
  type TimeRange,
  type Week,
} from './openingHoursWeek'

/**
 * opening-hours (SEN-81) — Google-Places opening periods, edited as a week.
 *
 * Storage is untouched: day 0 = Sunday, "HHMM", the composite the kind's codec
 * validates on the server. The editor is Monday-first (ISO_WEEK, declared once
 * in display/projections.ts): one row per day with an Open/Closed switch, time
 * pickers per range, "Add hours", "Open 24 hours", and copy-to-weekdays/all.
 * A range that closes at or before it opens runs past midnight and is labelled
 * so. Validation mirrors how monad.operating_zone will read the week
 * (openingHoursWeek.ts); the codec still has the final word.
 *
 * READ: the served declaration's default projection owns the cell text
 * (RenderedCell dispatches it before this widget). The read below is the
 * fallback for a surface that has no declaration in hand (the Types gallery),
 * and runs the SAME collapse-ranges strategy — there is no second formatter.
 */

/** Mirrors the `schedule` projection migrations 047 + 091 declare. Used only
 *  where no declaration is served; the served one always wins. */
const FALLBACK_SCHEDULE: ProjectionSpec = {
  strategy: 'collapse-ranges', entries: 'periods',
  axis: 'open.day', axis_kind: 'day-of-week',
  equal_on: ['open.time', 'close.time'],
  value_fields: ['open.time', 'close.time'], value_kind: 'hhmm-range',
  absent_text: 'closed',
}

const WEEKDAYS = ISO_WEEK.slice(0, 5)
const DEFAULT_RANGE: TimeRange = { open: '09:00', close: '17:00' }

function summarize(value: unknown, schedule: ProjectionSpec | null): string | null {
  if (value == null) return null
  return runProjection(value, schedule ?? FALLBACK_SCHEDULE)?.text ?? null
}

function OpeningHoursContent({ value }: CellContentProps) {
  const text = summarize(value, null)
  if (!text) return <span className="text-muted-foreground">—</span>
  return <span className="truncate" title={text}>{text}</span>
}

/** The roomy read (a record page's facts): the week as a tight day | hours list,
 *  days with the same hours merged, closed days muted, today's row marked, and
 *  "Open now" / "Closed now" on the tenant clock. A table cell keeps `read`'s line. */
function OpeningHoursDetail({ value }: CellContentProps) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(t)
  }, [])
  const parsed = weekFromValue(value)
  if (value == null || 'unsupported' in parsed) return <OpeningHoursContent value={value} />
  const zone = getTenantZone()
  const rows = weekRows(parsed.week)
  const { day: today } = localDayMinute(now, zone)
  const open = isOpenNow(parsed.week, now, zone)
  return (
    <div data-testid="opening-hours-detail" className="flex flex-col gap-1.5">
      <span data-testid="opening-hours-now"
        className={`w-fit rounded-full px-2 py-0.5 text-[11px] font-medium ${(open ? viewTheme.semantic.good : viewTheme.semantic.neutral).badgeClass}`}>
        {open ? 'Open now' : 'Closed now'}
      </span>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-sm">
        {rows.map((r) => {
          const isToday = r.members.includes(today)
          return (
            <div key={r.days} className="contents" data-testid="opening-hours-row" data-today={isToday || undefined}>
              <dt className={`tabular-nums ${isToday ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{r.days}</dt>
              <dd className={`tabular-nums ${r.hours ? (isToday ? 'font-medium text-foreground' : 'text-foreground') : 'text-muted-foreground'}`}>
                {r.hours ?? 'Closed'}
              </dd>
            </div>
          )
        })}
      </dl>
    </div>
  )
}

const toMinutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3))
const isOvernight = (r: TimeRange) => /^\d\d:\d\d$/.test(r.open) && /^\d\d:\d\d$/.test(r.close) && toMinutes(r.close) < toMinutes(r.open)
const closesAtMidnight = (r: TimeRange) => r.close === '00:00' && r.open !== '00:00'

/** The day as a 24-hour track: its own spans, plus the previous day's
 *  overnight spill (lighter) at the start of the morning. */
function DayTrack({ day, week }: { day: number; week: Week }) {
  const spans: Array<{ from: number; to: number; spill?: boolean }> = []
  const d = week[day]
  if (d.mode === '24h') spans.push({ from: 0, to: 1440 })
  if (d.mode === 'hours') {
    for (const r of d.ranges) {
      if (!/^\d\d:\d\d$/.test(r.open) || !/^\d\d:\d\d$/.test(r.close) || r.open === r.close) continue
      const o = toMinutes(r.open), c = toMinutes(r.close)
      spans.push({ from: o, to: c > o ? c : 1440 })
    }
  }
  const prev = week[(day + 6) % 7]
  if (prev.mode === 'hours') {
    for (const r of prev.ranges) {
      if (isOvernight(r) && r.close !== '00:00') spans.push({ from: 0, to: toMinutes(r.close), spill: true })
    }
  }
  return (
    <div aria-hidden="true" data-testid={`opening-hours-track-${day}`} className="relative h-1.5 w-full rounded-full bg-muted">
      {spans.map((s, i) => (
        <span
          key={i}
          className={`absolute inset-y-0 rounded-full ${s.spill ? 'bg-primary/40' : 'bg-primary'}`}
          style={{ left: `${(s.from / 1440) * 100}%`, width: `${((s.to - s.from) / 1440) * 100}%` }}
        />
      ))}
    </div>
  )
}

/** The same switch treatment SettingsForm's ToggleOption ships (role=switch). */
function OpenSwitch({ on, label, onChange }: { on: boolean; label: string; onChange: (on: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      className={`relative h-5 w-9 shrink-0 rounded-full border outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
        on ? 'border-primary bg-primary' : 'border-border bg-muted'
      }`}
    >
      <span
        className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-background shadow transition-transform ${
          on ? 'translate-x-4' : 'translate-x-0.5'
        }`}
        style={{ left: 0 }}
      />
    </button>
  )
}

const TIME_INPUT = `w-[7rem] rounded-sm bg-background px-1.5 py-1 text-sm tabular-nums outline-none [color-scheme:light] dark:[color-scheme:dark] ${controlChrome('form')}`

function DayRow({
  day,
  week,
  problems,
  onChange,
  onEnter,
}: {
  day: number
  week: Week
  problems: string[]
  onChange: (next: DayHours) => void
  onEnter: () => void
}) {
  const d = week[day]
  const name = dayName(day)
  const open = d.mode !== 'closed'

  function setOpen(on: boolean) {
    if (!on) return onChange({ mode: 'closed' })
    // Opening a day starts from the nearest earlier open day's hours — the
    // usual case is "same as yesterday" — else a plain 09–17.
    const idx = ISO_WEEK.indexOf(day)
    for (let i = idx - 1; i >= 0; i--) {
      const prev = week[ISO_WEEK[i]]
      if (prev.mode === 'hours') return onChange({ mode: 'hours', ranges: prev.ranges.map((r) => ({ ...r })) })
    }
    onChange({ mode: 'hours', ranges: [{ ...DEFAULT_RANGE }] })
  }
  function setRange(i: number, patch: Partial<TimeRange>) {
    if (d.mode !== 'hours') return
    onChange({ mode: 'hours', ranges: d.ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)) })
  }
  function removeRange(i: number) {
    if (d.mode !== 'hours') return
    const ranges = d.ranges.filter((_, j) => j !== i)
    onChange(ranges.length ? { mode: 'hours', ranges } : { mode: 'closed' })
  }
  function addRange() {
    if (d.mode !== 'hours') return
    const last = d.ranges[d.ranges.length - 1]
    // Start the new range an hour after the last one closes (e.g. after lunch).
    const start = last && /^\d\d:\d\d$/.test(last.close) ? Math.min(toMinutes(last.close) + 60, 22 * 60) : 13 * 60
    const hh = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
    onChange({ mode: 'hours', ranges: [...d.ranges, { open: hh(start), close: hh(Math.min(start + 4 * 60, 23 * 60 + 59)) }] })
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); onEnter() }
  }

  return (
    <div data-testid={`opening-hours-day-${day}`} className="border-b border-border py-1.5 last:border-b-0">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5 sm:flex-nowrap">
        <span className="w-9 shrink-0 pt-1 text-sm font-medium text-foreground">{name}</span>
        <div className="pt-0.5">
          <OpenSwitch on={open} label={`${name} open`} onChange={setOpen} />
        </div>
        <div className="order-last w-full min-w-0 sm:order-none sm:w-auto sm:flex-1">
          {d.mode === 'closed' && <div className="pt-1 text-sm text-muted-foreground">Closed</div>}
          {d.mode === '24h' && (
            <div className="flex items-center gap-2 pt-1 text-sm">
              <span className="text-foreground">Open 24 hours</span>
              <Button variant="bare" className="text-xs" onClick={() => onChange({ mode: 'hours', ranges: [{ ...DEFAULT_RANGE }] })}>
                Set hours
              </Button>
            </div>
          )}
          {d.mode === 'hours' && (
            <div className="space-y-1">
              {d.ranges.map((r, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <input
                    type="time"
                    step={60}
                    aria-label={`${name} opens at`}
                    data-testid={`opening-hours-open-${day}-${i}`}
                    value={r.open}
                    onChange={(e) => setRange(i, { open: e.target.value })}
                    onKeyDown={onKeyDown}
                    className={TIME_INPUT}
                  />
                  <span className="text-muted-foreground">–</span>
                  <input
                    type="time"
                    step={60}
                    aria-label={`${name} closes at`}
                    data-testid={`opening-hours-close-${day}-${i}`}
                    value={r.close}
                    onChange={(e) => setRange(i, { close: e.target.value })}
                    onKeyDown={onKeyDown}
                    className={TIME_INPUT}
                  />
                  <span className="w-10 shrink-0 text-2xs text-muted-foreground">
                    {closesAtMidnight(r) ? (
                      'midnight'
                    ) : isOvernight(r) ? (
                      <span data-testid={`opening-hours-overnight-${day}-${i}`} title={`Closes the next day (${dayName((day + 1) % 7)})`}>
                        +1 day
                      </span>
                    ) : null}
                  </span>
                  <span className="flex w-5 shrink-0 justify-center">
                    {d.ranges.length > 1 && (
                      <Button variant="ghost" size="iconSm" className="px-1" aria-label={`Remove ${name} ${r.open}–${r.close}`} onClick={() => removeRange(i)}>
                        ✕
                      </Button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="ml-auto flex w-14 shrink-0 items-center justify-end gap-1 pt-1 sm:ml-0">
          {d.mode === 'hours' && (
            <>
              <Button variant="ghost" size="iconSm" className="px-1" aria-label={`Add hours to ${name}`} title="Add hours" onClick={addRange}>
                +
              </Button>
              <Button variant="bare" className="text-xs" aria-label={`${name} open 24 hours`} title="Open 24 hours" onClick={() => onChange({ mode: '24h' })}>
                24h
              </Button>
            </>
          )}
        </div>
        <div className="hidden w-16 shrink-0 pt-3 sm:block">
          <DayTrack day={day} week={week} />
        </div>
      </div>
      {problems.map((p) => (
        <div key={p} role="alert" className="mt-1 text-[11px] text-destructive sm:pl-[5.25rem]">{p}</div>
      ))}
    </div>
  )
}

function OpeningHoursEditor({ value, onCommit, onCancel, context, slug, typeId, attributeId }: CellEditorProps) {
  const b = editorBehavior(context)
  const [open, setOpen] = useState(false)
  const initial = useMemo(() => weekFromValue(value), [value])
  const [week, setWeek] = useState<Week>(() => ('week' in initial ? initial.week : closedWeek()))
  // The day the copy actions copy FROM: the last day someone changed.
  const [source, setSource] = useState<number>(ISO_WEEK[0])

  const { data: attrs } = useQuery({ ...attributesQuery(slug!, typeId!), enabled: !!slug && !!typeId })
  const attribute = ((attrs ?? []) as unknown as Array<{ attribute_id: string; name?: string; display_spec?: unknown }>)
    .find((a) => a.attribute_id === attributeId)
  const schedule = defaultProjection(attribute?.display_spec)
  const label = attribute?.name ?? 'Opening hours'

  const problems = validateWeek(week)
  const next = valueFromWeek(week)
  const preview = problems.length === 0 ? summarize(next, schedule) : null
  const triggerText = summarize(value, schedule)

  function change(day: number, d: DayHours) {
    setSource(day)
    setWeek((w) => w.map((x, i) => (i === day ? d : x)))
  }
  function close() {
    if (b.controlledOpen) setOpen(false)
  }
  function cancel() {
    close()
    onCancel()
  }
  function save() {
    if (problems.length > 0) return
    close()
    const prior = (value as { periods?: unknown } | null)?.periods
    // Nothing changed → nothing to write (a save must not drop weekday_text
    // or add a timeline event for a value the person never altered).
    if (JSON.stringify(prior ?? null) === JSON.stringify(next.periods)) return onCancel()
    onCommit(next)
  }
  function clear() {
    close()
    onCommit(null)
  }

  return (
    <Popover.Root
      open={b.controlledOpen ? open : undefined}
      defaultOpen={!b.controlledOpen}
      onOpenChange={(o) => {
        if (b.controlledOpen) setOpen(o)
        if (!o && b.dismissCancels) onCancel()
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          data-testid="cell-editor"
          className={`flex w-full items-center gap-1.5 truncate rounded-sm px-2 py-1.5 text-left text-sm text-foreground outline-none ${controlChrome(context)}`}
          onClick={b.controlledOpen ? () => setOpen(true) : undefined}
        >
          {triggerText ?? (
            <span className="text-muted-foreground">{context === 'form' ? `Set ${label}…` : '—'}</span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          collisionPadding={12}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            // Escape cancels the draft and closes only this layer; it must not
            // also close the record stage behind it (editorChrome escapeIsLocal).
            if (e.key === 'Escape' && b.escapeIsLocal) escapeClosesOwnLayer(cancel)(e)
            else if (e.key === 'Escape') cancel()
          }}
          data-testid="opening-hours-editor"
          className="z-[100] max-h-[var(--radix-popover-content-available-height)] w-[38rem] max-w-[calc(100vw-24px)] overflow-y-auto rounded-float border border-border bg-background p-4 shadow-float"
        >
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <h3 className="text-sm font-semibold text-foreground">{label}</h3>
            <span className="text-2xs text-muted-foreground">Local time at the site</span>
          </div>

          {'unsupported' in initial ? (
            <div data-testid="opening-hours-unsupported" className="space-y-2 text-sm">
              <p className="text-foreground">These hours can’t be edited as a week: {initial.unsupported}.</p>
              <p className="text-muted-foreground">They are kept as they are. Clear them to start a new week.</p>
            </div>
          ) : (
            <>
              <div>
                {ISO_WEEK.map((day) => (
                  <DayRow
                    key={day}
                    day={day}
                    week={week}
                    problems={problems.filter((p) => p.day === day).map((p) => p.message)}
                    onChange={(d) => change(day, d)}
                    onEnter={save}
                  />
                ))}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>Copy {dayName(source)}’s hours to</span>
                <Button variant="outline" data-testid="opening-hours-copy-weekdays" onClick={() => setWeek((w) => copyDay(w, source, WEEKDAYS))}>
                  Weekdays
                </Button>
                <Button variant="outline" data-testid="opening-hours-copy-all" onClick={() => setWeek((w) => copyDay(w, source, ISO_WEEK))}>
                  All days
                </Button>
              </div>
              <div className="mt-3 rounded-md bg-muted/60 px-3 py-2 text-sm" data-testid="opening-hours-preview">
                {preview ? (
                  <span className="text-foreground">{preview}</span>
                ) : (
                  <span role="alert" className="text-destructive">
                    {problems.find((p) => p.day === null)?.message ?? 'Fix the problems above to save.'}
                  </span>
                )}
              </div>
            </>
          )}

          <div className="mt-3 flex items-center gap-2">
            {value != null && (
              <Button variant="quiet" data-testid="opening-hours-clear" onClick={clear}>
                Clear
              </Button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Button variant="quiet" data-testid="opening-hours-cancel" onClick={cancel}>
                Cancel
              </Button>
              {'week' in initial && (
                <Button variant="primary" size="sm" data-testid="opening-hours-save" disabled={problems.length > 0} onClick={save}>
                  Save
                </Button>
              )}
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

registerWidget('opening-hours', {
  read: OpeningHoursContent, detail: OpeningHoursDetail, write: OpeningHoursEditor, defaultWidth: 280,
  // The server returns the place's hours as this kind's cell value (Google's
  // periods, unchanged), so a picked place fills them as-is.
  fromPlace: (place) => place.opening_periods ?? undefined,
})
